import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Avatar,
  Chip,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Card,
  Fade,
  alpha,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Close,
  ArrowBack,
  CalendarMonth,
  Person,
  Groups,
  Search
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { AttendanceStatus, InformationType } from '../../types';

// ============================================================================
// Student MarkAttendance Modal
//
// Mirrors components/attendance/MarkAttendanceModal (employee) so the two
// flows feel like cousins. Key deltas:
//   - works inside an already-chosen (batch, class, section) scope from the page
//   - searches Students, not Users
//   - no Scheduled-Off / Not-Scheduled modes — those statuses come from the
//     server-side weekly-off + holiday overlay, never manual marks
// ============================================================================

export interface StudentData {
  _id: string;
  name: string;
  rollNumber: string;
  fatherName?: string;
  phone?: string;
}

export interface StudentAttendanceFormData {
  studentId: string;
  status: AttendanceStatus;
  informationType?: InformationType;
  remarks?: string;
}

export interface StudentSavePayload {
  attendanceRecords: StudentAttendanceFormData[];
  applyToAll: boolean;
  bulkStatus?: AttendanceStatus | null;
  bulkInfoType?: InformationType | null;
  bulkRemarks?: string;
}

type AttendanceMode = 'select' | 'individual' | 'bulk';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onSave: (payload: StudentSavePayload) => Promise<void>;
  onSearch: (query: string) => Promise<StudentData[]>;
  scopeLabel?: string;  // e.g. "Class 10 · Section A" — shown for context
}

function useDebounce<T extends (...args: any[]) => void>(callback: T, delay: number) {
  const callbackRef = useRef(callback);
  useMemo(() => { callbackRef.current = callback; }, [callback]);

  return useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    const func: any = (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => callbackRef.current(...args), delay);
    };
    func.cancel = () => clearTimeout(timer);
    return func as ((...args: any[]) => void) & { cancel: () => void };
  }, [delay]);
}

const STATUS_OPTIONS: { value: AttendanceStatus; label: string; color: string }[] = [
  { value: AttendanceStatus.PRESENT, label: 'Present', color: '#22c55e' },
  { value: AttendanceStatus.LATE, label: 'Late', color: '#f59e0b' },
  { value: AttendanceStatus.HALF_DAY, label: 'Half Day', color: '#3b82f6' },
  { value: AttendanceStatus.ABSENT, label: 'Absent', color: '#ef4444' }
];

const MODE_OPTIONS = [
  { id: 'individual' as const, title: 'Mark Individual', desc: 'Select a single student', icon: Person, color: '#2563eb' },
  { id: 'bulk' as const, title: 'Mark Bulk', desc: 'Select multiple students or apply to everyone', icon: Groups, color: '#7c3aed' }
];

const StudentMarkAttendanceModal: React.FC<Props> = ({
  open,
  onClose,
  selectedDate,
  onDateChange,
  onSave,
  onSearch,
  scopeLabel
}) => {
  const [mode, setMode] = useState<AttendanceMode>('select');

  // Individual
  const [selectedIndividual, setSelectedIndividual] = useState<StudentData | null>(null);
  const [individualData, setIndividualData] = useState<StudentAttendanceFormData>({
    studentId: '',
    status: AttendanceStatus.PRESENT
  });

  // Bulk
  const [selectedStudents, setSelectedStudents] = useState<StudentData[]>([]);
  const [selectAllActive, setSelectAllActive] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<AttendanceStatus | null>(null);
  const [bulkInfoType, setBulkInfoType] = useState<InformationType | null>(null);
  const [bulkRemarks, setBulkRemarks] = useState('');

  // Search
  const [searchResults, setSearchResults] = useState<StudentData[]>([]);
  const [searching, setSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);

  const resetAll = useCallback(() => {
    setMode('select');
    setSelectedIndividual(null);
    setIndividualData({ studentId: '', status: AttendanceStatus.PRESENT });
    setSelectedStudents([]);
    setSelectAllActive(false);
    setBulkStatus(null);
    setBulkInfoType(null);
    setBulkRemarks('');
    setInputValue('');
    setSearchResults([]);
    setSearching(false);
  }, []);

  useEffect(() => {
    if (!open) resetAll();
  }, [open, resetAll]);

  const performSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const results = await onSearch(q);
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [onSearch]);

  const debouncedSearch = useDebounce(performSearch, 350);

  useEffect(() => {
    if (inputValue) debouncedSearch(inputValue);
    else setSearchResults([]);
    return () => debouncedSearch.cancel();
  }, [inputValue, debouncedSearch]);

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleAddStudent = (s: StudentData) => {
    if (selectAllActive) return;
    if (!selectedStudents.find(x => x._id === s._id)) {
      setSelectedStudents(prev => [...prev, s]);
    }
    setInputValue('');
  };

  const handleRemoveStudent = (id: string) => {
    if (selectAllActive) return;
    setSelectedStudents(prev => prev.filter(s => s._id !== id));
  };

  const handleIndividualSelect = (s: StudentData | null) => {
    setSelectedIndividual(s);
    if (s) {
      setIndividualData({ studentId: s._id, status: AttendanceStatus.PRESENT });
      setInputValue('');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload: StudentSavePayload = {
        attendanceRecords: [],
        applyToAll: false
      };

      if (mode === 'individual' && selectedIndividual) {
        payload.attendanceRecords = [{ ...individualData, studentId: selectedIndividual._id }];
      } else if (mode === 'bulk') {
        if (selectAllActive) {
          payload.applyToAll = true;
          payload.bulkStatus = bulkStatus;
          payload.bulkInfoType = bulkInfoType;
          payload.bulkRemarks = bulkRemarks;
        } else {
          payload.attendanceRecords = selectedStudents.map(s => ({
            studentId: s._id,
            status: bulkStatus!,
            informationType: bulkInfoType || undefined,
            remarks: bulkRemarks
          }));
        }
      }

      await onSave(payload);
      handleClose();
    } catch {
      // Page-level snackbar surfaces the message — keep modal open so the
      // user doesn't lose their selections.
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled = useMemo(() => {
    if (mode === 'select') return true;
    if (mode === 'individual') {
      if (!selectedIndividual) return true;
      if ([AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(individualData.status) && !individualData.informationType) return true;
      return false;
    }
    if (mode === 'bulk') {
      if (!selectAllActive && selectedStudents.length === 0) return true;
      if (!bulkStatus) return true;
      if ([AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(bulkStatus) && !bulkInfoType) return true;
      return false;
    }
    return true;
  }, [mode, selectedIndividual, individualData, selectAllActive, selectedStudents, bulkStatus, bulkInfoType]);

  // ---- Render helpers (kept narrow — same pattern as the employee modal) ----

  const renderSearchField = (
    onSelect: (s: StudentData) => void,
    placeholder: string,
    disabled = false
  ) => (
    <Autocomplete
      options={searchResults}
      getOptionLabel={(o) => `${o.name} · ${o.rollNumber}`}
      filterOptions={(x) => x}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, val, reason) => {
        if (reason === 'reset' && !val) return;
        setInputValue(val);
      }}
      onChange={(_, val) => { if (val) onSelect(val); }}
      disabled={disabled}
      fullWidth
      loading={searching}
      clearOnBlur={false}
      noOptionsText={
        !inputValue ? 'Type a name or roll number…' :
        searching ? 'Searching…' :
        'No students found'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {searching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6)
            }
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props as any;
        return (
          <li key={option._id} {...rest}>
            <Box display="flex" alignItems="center" gap={1.5} py={0.5} width="100%">
              <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: 'primary.light' }}>
                {option.name[0]}
              </Avatar>
              <Box flex={1} minWidth={0}>
                <Typography variant="body2" fontWeight={500} noWrap>{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Roll #{option.rollNumber}{option.fatherName ? ` · ${option.fatherName}` : ''}
                </Typography>
              </Box>
            </Box>
          </li>
        );
      }}
    />
  );

  const renderModeSelect = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      {MODE_OPTIONS.map(opt => (
        <Card
          key={opt.id}
          onClick={() => setMode(opt.id)}
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: opt.color,
              bgcolor: alpha(opt.color, 0.04)
            }
          }}
        >
          <Avatar sx={{ bgcolor: alpha(opt.color, 0.1), color: opt.color }}>
            <opt.icon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>{opt.title}</Typography>
            <Typography variant="body2" color="text.secondary">{opt.desc}</Typography>
          </Box>
        </Card>
      ))}
    </Box>
  );

  const renderIndividual = () => (
    <Fade in timeout={300}>
      <Box sx={{ pt: 1 }}>
        {!selectedIndividual ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>Find Student</Typography>
            <Box sx={{ maxWidth: 420, mx: 'auto' }}>
              {renderSearchField(handleIndividualSelect, 'Search by name or roll number…')}
            </Box>
          </Box>
        ) : (
          <Box>
            <Card variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 50, height: 50, bgcolor: 'primary.main' }}>{selectedIndividual.name[0]}</Avatar>
              <Box flex={1}>
                <Typography variant="h6" fontSize="1rem">{selectedIndividual.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Roll #{selectedIndividual.rollNumber}
                  {selectedIndividual.fatherName ? ` · S/O ${selectedIndividual.fatherName}` : ''}
                </Typography>
              </Box>
              <Button size="small" onClick={() => setSelectedIndividual(null)} sx={{ borderRadius: 2 }}>
                Change
              </Button>
            </Card>

            <Typography variant="subtitle2" gutterBottom>Status</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {STATUS_OPTIONS.map(opt => (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  onClick={() => setIndividualData(prev => ({ ...prev, status: opt.value }))}
                  variant={individualData.status === opt.value ? 'filled' : 'outlined'}
                  sx={{
                    bgcolor: individualData.status === opt.value ? opt.color : 'transparent',
                    color: individualData.status === opt.value ? '#fff' : opt.color,
                    borderColor: opt.color,
                    '&:hover': { bgcolor: individualData.status === opt.value ? opt.color : alpha(opt.color, 0.1) }
                  }}
                />
              ))}
            </Box>

            {[AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(individualData.status) && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={500} gutterBottom>Type</Typography>
                <RadioGroup
                  row
                  value={individualData.informationType || ''}
                  onChange={(e) => setIndividualData(prev => ({ ...prev, informationType: e.target.value as InformationType }))}
                >
                  <FormControlLabel value={InformationType.INFORMED} control={<Radio size="small" />} label="Informed" />
                  <FormControlLabel value={InformationType.UNINFORMED} control={<Radio size="small" />} label="Uninformed" />
                </RadioGroup>
              </Box>
            )}

            <TextField
              fullWidth
              placeholder="Optional remarks"
              multiline
              rows={2}
              value={individualData.remarks || ''}
              onChange={(e) => setIndividualData(prev => ({ ...prev, remarks: e.target.value }))}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        )}
      </Box>
    </Fade>
  );

  const renderBulk = () => (
    <Box sx={{ pt: 1 }}>
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={<Checkbox checked={selectAllActive} onChange={(e) => setSelectAllActive(e.target.checked)} />}
          label={<Typography fontWeight={500}>Apply to everyone in this class</Typography>}
        />
        {scopeLabel && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 3.5, mt: -0.5 }}>
            Scope: {scopeLabel}
          </Typography>
        )}
      </Box>

      {!selectAllActive && (
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>Add Students</Typography>
          {renderSearchField(handleAddStudent, 'Search to add students…')}
          <Box display="flex" flexWrap="wrap" gap={0.5} mt={1.5}>
            {selectedStudents.map(s => (
              <Chip key={s._id} label={`${s.name} · ${s.rollNumber}`} onDelete={() => handleRemoveStudent(s._id)} size="small" />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" gutterBottom>Bulk Status</Typography>
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {STATUS_OPTIONS.map(opt => (
          <Chip
            key={opt.value}
            label={opt.label}
            onClick={() => setBulkStatus(opt.value)}
            variant={bulkStatus === opt.value ? 'filled' : 'outlined'}
            sx={{
              bgcolor: bulkStatus === opt.value ? opt.color : 'transparent',
              color: bulkStatus === opt.value ? '#fff' : opt.color,
              borderColor: opt.color,
              '&:hover': { bgcolor: bulkStatus === opt.value ? opt.color : alpha(opt.color, 0.1) }
            }}
          />
        ))}
      </Box>

      {bulkStatus && [AttendanceStatus.LATE, AttendanceStatus.HALF_DAY, AttendanceStatus.ABSENT].includes(bulkStatus) && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body2" fontWeight={500}>Information:</Typography>
          <Button
            size="small"
            variant={bulkInfoType === InformationType.INFORMED ? 'contained' : 'outlined'}
            onClick={() => setBulkInfoType(InformationType.INFORMED)}
            sx={{ borderRadius: 2 }}
          >
            Informed
          </Button>
          <Button
            size="small"
            variant={bulkInfoType === InformationType.UNINFORMED ? 'contained' : 'outlined'}
            onClick={() => setBulkInfoType(InformationType.UNINFORMED)}
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Uninformed
          </Button>
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Optional bulk remarks"
        value={bulkRemarks}
        onChange={(e) => setBulkRemarks(e.target.value)}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '85vh', maxWidth: 720 } }}
    >
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
        {mode !== 'select' && (
          <IconButton onClick={() => resetAll()} size="small" edge="start">
            <ArrowBack />
          </IconButton>
        )}
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700} fontSize="1.1rem">
            {mode === 'select' ? 'Mark Student Attendance' : MODE_OPTIONS.find(m => m.id === mode)?.title}
          </Typography>
          {scopeLabel && mode === 'select' && (
            <Typography variant="caption" color="text.secondary">{scopeLabel}</Typography>
          )}
        </Box>
        <DatePicker
          value={selectedDate}
          onChange={(d) => d && onDateChange(d)}
          format="dd MMM yyyy"
          slotProps={{ textField: { size: 'small', sx: { width: 180 } } }}
          slots={{ openPickerIcon: CalendarMonth }}
        />
        <IconButton onClick={handleClose} size="small" edge="end">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 400 }}>
        {mode === 'select' && renderModeSelect()}
        {mode === 'individual' && renderIndividual()}
        {mode === 'bulk' && renderBulk()}
      </DialogContent>

      {mode !== 'select' && (
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleClose} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled || saving}
            sx={{ borderRadius: 2, px: 4 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default StudentMarkAttendanceModal;
